import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Snowflake, Flame, Fan, Power, Droplets } from 'lucide-react';
import type { HVACSystem } from '@/types';

interface HVACPanelProps {
  system: HVACSystem;
  onUpdate?: (settings: Partial<HVACSystem>) => void;
}

export function HVACPanel({ system, onUpdate }: HVACPanelProps) {
  const [localTemp, setLocalTemp] = useState(system.targetTemp);

  useEffect(() => {
    setLocalTemp(system.targetTemp);
  }, [system.targetTemp]);

  const handleTempChange = (value: number[]) => {
    setLocalTemp(value[0]);
  };

  const handleTempCommit = () => {
    onUpdate?.({ targetTemp: localTemp });
  };

  const handleModeChange = (mode: 'cool' | 'heat' | 'fan' | 'off') => {
    onUpdate?.({ mode });
  };

  const handleFanSpeedChange = (fanSpeed: 'low' | 'med' | 'high') => {
    onUpdate?.({ fanSpeed });
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'cool': return <Snowflake className="h-4 w-4" />;
      case 'heat': return <Flame className="h-4 w-4" />;
      case 'fan': return <Fan className="h-4 w-4" />;
      default: return <Power className="h-4 w-4" />;
    }
  };

  const getModeColor = (mode: string) => {
    switch (mode) {
      case 'cool': return 'text-cyan-400';
      case 'heat': return 'text-orange-400';
      case 'fan': return 'text-blue-400';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Card className="lg:min-h-[647px] flex flex-col mb-4 lg:mb-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={getModeColor(system.mode)}>
              {getModeIcon(system.mode)}
            </div>
            <CardTitle>Mr Cool HVAC</CardTitle>
          </div>
          <Badge variant={system.enabled ? 'success' : 'secondary'}>
            {system.enabled ? 'Running' : 'Off'}
          </Badge>
        </div>
        <CardDescription>Climate control system</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 flex-1">
        {/* Current Temperature */}
        <div className="text-center">
          <div className="text-5xl font-bold mb-2">{system.temperature}°F</div>
          <div className="text-sm text-muted-foreground">Current Temperature</div>
          {system.humidity !== undefined && (
            <div className="flex items-center justify-center gap-2 mt-2 text-muted-foreground">
              <Droplets className="h-4 w-4" />
              <span>{system.humidity}% humidity</span>
            </div>
          )}
        </div>

        {/* Target Temperature */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Target Temperature</span>
            <span className="text-2xl font-bold">{localTemp}°F</span>
          </div>
          <Slider
            value={[localTemp]}
            onValueChange={handleTempChange}
            onValueCommit={handleTempCommit}
            min={60}
            max={85}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>60°F</span>
            <span>85°F</span>
          </div>
        </div>

        {/* Mode Control */}
        <div className="space-y-3">
          <span className="text-sm font-medium">Mode</span>
          <div className="grid grid-cols-4 gap-2">
            <Button
              variant={system.mode === 'cool' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleModeChange('cool')}
              className="flex flex-col items-center gap-1 h-auto py-3"
            >
              <Snowflake className="h-4 w-4" />
              <span className="text-xs">Cool</span>
            </Button>
            <Button
              variant={system.mode === 'heat' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleModeChange('heat')}
              className="flex flex-col items-center gap-1 h-auto py-3"
            >
              <Flame className="h-4 w-4" />
              <span className="text-xs">Heat</span>
            </Button>
            <Button
              variant={system.mode === 'fan' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleModeChange('fan')}
              className="flex flex-col items-center gap-1 h-auto py-3"
            >
              <Fan className="h-4 w-4" />
              <span className="text-xs">Fan</span>
            </Button>
            <Button
              variant={system.mode === 'off' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleModeChange('off')}
              className="flex flex-col items-center gap-1 h-auto py-3"
            >
              <Power className="h-4 w-4" />
              <span className="text-xs">Off</span>
            </Button>
          </div>
        </div>

        {/* Fan Speed */}
        <div className="space-y-3">
          <span className="text-sm font-medium">Fan Speed</span>
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant={system.fanSpeed === 'low' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFanSpeedChange('low')}
            >
              Low
            </Button>
            <Button
              variant={system.fanSpeed === 'med' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFanSpeedChange('med')}
            >
              Med
            </Button>
            <Button
              variant={system.fanSpeed === 'high' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFanSpeedChange('high')}
            >
              High
            </Button>
          </div>
        </div>

        {/* System Status */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">System Status</span>
            <span className={`text-sm ${
              Math.abs(system.temperature - system.targetTemp) <= 2
                ? 'text-success'
                : 'text-warning'
            }`}>
              {Math.abs(system.temperature - system.targetTemp) <= 2
                ? 'At Target'
                : system.temperature > system.targetTemp
                  ? 'Cooling'
                  : 'Heating'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground mb-1">Temperature Diff</div>
              <div className="font-medium">
                {system.temperature > system.targetTemp ? '+' : ''}
                {(system.temperature - system.targetTemp).toFixed(1)}°F
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Humidity</div>
              <div className="font-medium flex items-center gap-1">
                <Droplets className="h-3.5 w-3.5" />
                {system.humidity || 45}%
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
